import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../../styles/accessibility.css';
import './OperationsManagement.css';

const OperationsManagement = () => {
  const [metrics, setMetrics] = useState({
    operationalEfficiency: 87.5,
    productionUptime: 94.2,
    qualityScore: 91.8,
    resourceUtilization: 88.3,
    complianceRate: 96.7,
    sustainabilityIndex: 82.4
  });

  const [recentActivities, setRecentActivities] = useState([
    { id: 1, type: 'production', title: 'Production Line A - Optimization Complete', time: '15 mins ago', status: 'success' },
    { id: 2, type: 'quality', title: 'Quality Inspection Batch #2847', time: '1 hour ago', status: 'success' },
    { id: 3, type: 'maintenance', title: 'Scheduled Maintenance - Unit 3', time: '2 hours ago', status: 'warning' },
    { id: 4, type: 'compliance', title: 'ESG Compliance Audit Passed', time: '3 hours ago', status: 'success' },
    { id: 5, type: 'resource', title: 'Resource Allocation Updated', time: '4 hours ago', status: 'info' }
  ]);

  const [alerts, setAlerts] = useState([
    { id: 1, severity: 'warning', message: 'Production Line B efficiency below target (82%)', time: '30 mins ago' },
    { id: 2, severity: 'info', message: 'Scheduled maintenance window tonight 10 PM - 2 AM', time: '2 hours ago' }
  ]);

  useEffect(() => {
    document.title = 'Operations Management - Bread Department';
  }, []);

  const getMetricColor = (value) => {
    if (value >= 90) return 'success';
    if (value >= 80) return 'info';
    if (value >= 70) return 'warning';
    return 'danger';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✓';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      case 'danger': return '✗';
      default: return '•';
    }
  };

  const modules = [
    {
      title: 'Production Analytics',
      description: 'Real-time production metrics, efficiency tracking, and performance analytics',
      icon: '📊',
      link: '/bread/operations-management/production-analytics',
      color: 'primary',
      metrics: ['Efficiency', 'Uptime', 'Output']
    },
    {
      title: 'Quality Control',
      description: 'Quality assurance processes, inspection results, and standards compliance',
      icon: '✓',
      link: '/bread/quality-control',
      color: 'success',
      metrics: ['Quality Score', 'Pass Rate', 'Defect Rate']
    },
    {
      title: 'Resource Management',
      description: 'Resource allocation, inventory tracking, and capacity planning',
      icon: '📦',
      link: '/bread/operations-management/resources',
      color: 'info',
      metrics: ['Utilization', 'Inventory', 'Capacity']
    },
    {
      title: 'Compliance Monitoring',
      description: 'ESG compliance tracking, regulatory adherence, and audit management',
      icon: '📋',
      link: '/bread/compliance-tools',
      color: 'warning',
      metrics: ['Compliance Rate', 'Audit Score', 'Risk Level']
    }
  ];

  return (
    <div className="container-fluid py-4 operations-management">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="display-6 fw-bold mb-2">Operations Management</h1>
              <p className="text-muted mb-0">
                Comprehensive operations oversight and performance monitoring for Bread Department
              </p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-primary btn-sm">
                <i className="bi bi-download"></i> Export Report
              </button>
              <button className="btn btn-primary btn-sm">
                <i className="bi bi-arrow-clockwise"></i> Refresh Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0">Active Alerts</h5>
              </div>
              <div className="card-body p-0">
                {alerts.map(alert => (
                  <div key={alert.id} className={`alert alert-${alert.severity} mb-0 rounded-0 border-0 border-start border-${alert.severity} border-4`}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{alert.message}</strong>
                      </div>
                      <small className="text-muted">{alert.time}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Dashboard */}
      <div className="row g-3 mb-4">
        <div className="col-md-6 col-lg-4 col-xl-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="text-muted text-uppercase small mb-0">Operational Efficiency</h6>
                <span className={`badge bg-${getMetricColor(metrics.operationalEfficiency)}`}>
                  {metrics.operationalEfficiency}%
                </span>
              </div>
              <h3 className="mb-0 fw-bold">{metrics.operationalEfficiency}%</h3>
              <small className="text-success">↑ 2.3% from last week</small>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 col-xl-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="text-muted text-uppercase small mb-0">Production Uptime</h6>
                <span className={`badge bg-${getMetricColor(metrics.productionUptime)}`}>
                  {metrics.productionUptime}%
                </span>
              </div>
              <h3 className="mb-0 fw-bold">{metrics.productionUptime}%</h3>
              <small className="text-success">↑ 1.8% from last week</small>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 col-xl-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="text-muted text-uppercase small mb-0">Quality Score</h6>
                <span className={`badge bg-${getMetricColor(metrics.qualityScore)}`}>
                  {metrics.qualityScore}%
                </span>
              </div>
              <h3 className="mb-0 fw-bold">{metrics.qualityScore}%</h3>
              <small className="text-success">↑ 0.5% from last week</small>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 col-xl-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="text-muted text-uppercase small mb-0">Resource Utilization</h6>
                <span className={`badge bg-${getMetricColor(metrics.resourceUtilization)}`}>
                  {metrics.resourceUtilization}%
                </span>
              </div>
              <h3 className="mb-0 fw-bold">{metrics.resourceUtilization}%</h3>
              <small className="text-warning">↓ 0.3% from last week</small>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 col-xl-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="text-muted text-uppercase small mb-0">Compliance Rate</h6>
                <span className={`badge bg-${getMetricColor(metrics.complianceRate)}`}>
                  {metrics.complianceRate}%
                </span>
              </div>
              <h3 className="mb-0 fw-bold">{metrics.complianceRate}%</h3>
              <small className="text-success">↑ 1.2% from last week</small>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 col-xl-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="text-muted text-uppercase small mb-0">Sustainability Index</h6>
                <span className={`badge bg-${getMetricColor(metrics.sustainabilityIndex)}`}>
                  {metrics.sustainabilityIndex}%
                </span>
              </div>
              <h3 className="mb-0 fw-bold">{metrics.sustainabilityIndex}%</h3>
              <small className="text-success">↑ 3.1% from last week</small>
            </div>
          </div>
        </div>
      </div>

      {/* Operations Modules */}
      <div className="row mb-4">
        <div className="col-12">
          <h4 className="mb-3">Operations Modules</h4>
        </div>
        {modules.map((module, index) => (
          <div key={index} className="col-md-6 col-lg-3 mb-3">
            <Link to={module.link} className="text-decoration-none">
              <div className={`card shadow-sm border-0 h-100 hover-card border-top border-${module.color} border-3`}>
                <div className="card-body">
                  <div className="d-flex align-items-start mb-3">
                    <div className="module-icon me-3" style={{ fontSize: '2rem' }}>
                      {module.icon}
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="card-title mb-1">{module.title}</h5>
                      <p className="card-text text-muted small mb-2">{module.description}</p>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {module.metrics.map((metric, idx) => (
                      <span key={idx} className={`badge bg-${module.color} bg-opacity-10 text-${module.color} small`}>
                        {metric}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="row g-4">
        <div className="col-lg-8">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">Recent Activity</h5>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {recentActivities.map(activity => (
                  <div key={activity.id} className="list-group-item list-group-item-action">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-start">
                        <span className={`badge bg-${activity.status} me-2 mt-1`}>
                          {getStatusIcon(activity.status)}
                        </span>
                        <div>
                          <h6 className="mb-1">{activity.title}</h6>
                          <small className="text-muted">
                            <span className="badge bg-secondary bg-opacity-10 text-secondary me-2">
                              {activity.type}
                            </span>
                            {activity.time}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-footer bg-white border-top text-center">
              <Link to="/bread/operations-management/activity" className="text-decoration-none">
                View All Activity →
              </Link>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <Link to="/bread/operations-management/production-analytics" className="btn btn-outline-primary text-start">
                  <i className="bi bi-graph-up me-2"></i>
                  View Production Analytics
                </Link>
                <Link to="/bread/quality-control" className="btn btn-outline-success text-start">
                  <i className="bi bi-check-circle me-2"></i>
                  Quality Control Dashboard
                </Link>
                <Link to="/bread/compliance-tools" className="btn btn-outline-warning text-start">
                  <i className="bi bi-clipboard-check me-2"></i>
                  Compliance Monitoring
                </Link>
                <button className="btn btn-outline-info text-start">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Generate Operations Report
                </button>
                <button className="btn btn-outline-secondary text-start">
                  <i className="bi bi-gear me-2"></i>
                  Operations Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationsManagement;
